<script setup>
import { defineEmits, defineProps } from 'vue';
import CatalogueProductCard from './CatalogueProductCard.vue';

/**
 * Un elenco di prodotti del catalogo ZDHC fra cui scegliere.
 *
 * Vive a parte perche' serve in due punti che mostrano la stessa cosa e la
 * decidono in modo diverso: i candidati che la ricerca automatica ha trovato, e
 * i risultati di una ricerca fatta a mano. Tenerne due copie significherebbe
 * che la prossima volta che si aggiunge un dato al riquadro se ne aggiorna una
 * e l'altra resta indietro.
 *
 * Non decide niente: mostra e riporta cosa e' stato scelto.
 */

const emits = defineEmits(['choose']);
const props = defineProps({
    products: { required: true, type: Array },
    // Il `product_reference_id` in corso di salvataggio, se c'e': serve a
    // spegnere la lista e a dire su quale riga si sta lavorando.
    submitting: { required: false, default: null },
    // Il prodotto per cui e' aperta una conferma: la sua scheda smette di
    // essere un comando, o un secondo clic involontario varrebbe come procedi.
    confirming: { required: false, default: null },
    // La scelta e' in due tempi: un clic seleziona e una conferma a parte
    // decide. Dichiararlo serve a `aria-pressed`, che deve valere `false` da
    // subito su ogni scheda: se comparisse solo dopo il primo clic, le schede
    // verrebbero annunciate prima come pulsanti normali e poi come interruttori.
    selectable: { required: false, type: Boolean, default: false },
    // Da dove vengono questi prodotti. Serve a distinguerli: lo stesso prodotto
    // puo' comparire fra i proposti **e** fra i risultati di una ricerca, e le
    // due copie non sono la stessa scelta — si registrano su endpoint diversi.
    source: { required: false, default: null },
    // La chiave selezionata, nella forma `provenienza:id`. La lista la mostra,
    // non la decide.
    selected: { required: false, default: null },
});

const keyFor = (product) => `${props.source}:${product.product_reference_id}`;

</script>
<template>
    <!-- Non scorre: lo scorrimento lo fornisce chi la usa. Piu' elenchi nella
         stessa schermata devono scorrere insieme, o si perde il conto di dove si
         era arrivati. -->
    <ul class="flex flex-col space-y-2">
        <li v-for="product in props.products" :key="product.product_reference_id">
            <!-- Il verde e' il colore con cui il portale dice "selezionato":
                 le caselle della tabella prodotti, la pagina corrente della
                 paginazione. Il blu pieno di prima era il colore delle azioni
                 primarie, e faceva sembrare la scheda scelta un pulsante da
                 premere. Il segno di spunta accompagna il colore: lo stato non
                 si affida al solo colore.

                 La selezione si legge dal fondo, non da un contorno spesso: un
                 anello verde chiaro su bianco sta a 2:1 e si vedeva male.

                 Il fuoco resta blu come su ogni altro elemento interattivo del
                 portale — e non si puo' lasciare al browser, perche' `index.css`
                 spegne l'outline di ogni pulsante con un `!important`. Blu sul
                 fuoco e verde sulla selezione sono due significati diversi: dove
                 sono le mani, e cosa e' stato scelto. Per questo il verde resta
                 verde **anche sul tema scuro**, dove il portale altrove lo scambia
                 con il blu: scambiandolo qui, una scheda selezionata e a fuoco
                 avrebbe avuto lo stesso bordo in entrambi i casi, e il fuoco non
                 si sarebbe visto. Il verde su fondo scuro sta a 8:1.

                 Il bordo grigio del tema scuro sta nel ramo "non selezionata" e
                 non fra le classi fisse: un `dark:` vince su una classe semplice
                 a prescindere dall'ordine in cui si scrivono, quindi convivendo
                 con il verde lo copriva — e la scheda selezionata restava
                 grigia come le altre. -->
            <button
                class="w-full text-left border rounded-md p-3 flex flex-col space-y-1 duration-300 hover:border-blue-500 focus:border-blue-500 dark:hover:border-blue-400 dark:focus:border-blue-400"
                :class="[
                    {'opacity-50' : props.submitting !== null && props.submitting !== keyFor(product)},
                    props.selected === keyFor(product)
                        ? 'border-light-green-500 dark:border-light-green-500 bg-light-green-100 dark:bg-gray-800'
                        : 'dark:border-gray-700'
                ]"
                :aria-pressed="props.selectable ? props.selected === keyFor(product) : null"
                :disabled="props.submitting !== null || props.confirming === product.product_reference_id"
                :aria-busy="props.submitting === keyFor(product)"
                :data-reference="product.product_reference_id"
                @click.prevent="emits('choose', product)">
                <CatalogueProductCard :product="product">
                    <template #heading>
                        <span class="flex items-center justify-between space-x-2">
                            <span class="font-semibold capitalize">{{ product.product_name ?? '-' }}</span>
                            <span
                                v-if="props.selected === keyFor(product)"
                                class="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center text-gray-800 bg-light-green-500 border-light-green-500">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 fill-current"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>
                            </span>
                        </span>
                    </template>
                </CatalogueProductCard>
            </button>

            <!-- Quello che segue la scheda — un avviso, una conferma — lo decide
                 chi usa la lista: qui non si sa quali conseguenze abbia una
                 scelta. -->
            <slot name="after" :product="product"></slot>
        </li>

    </ul>
</template>
